// hooks/useBadgeUpdater.js
import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateUnreadNotificationsCount } from "../redux/actions/notificationActions";
import { getUnreadNotificationsCount } from "../api/notificationAPI";
import { setUnreadMessagesCount } from "../redux/actions/messageActions";
import { useChatContext } from "stream-chat-react";

export const useBadgeUpdater = (
  updateBadge = async () => {},
  badgeCleared,
  setBadgeCleared,
  skipNotifications = false,
) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.loggedUser?.user?.id);
  // Keep refs at hook level so they persist across all re-renders
  const lastMessagesCount = useRef(null);
  const lastNotificationsCount = useRef(null);

  const { client } = useChatContext(); // get Stream Chat client from context

  const updateAllBadges = useCallback(async () => {
    if (!userId || !client) return;

    if (badgeCleared && typeof setBadgeCleared === "function") {
      setBadgeCleared(false);
      return;
    }

    let messagesUnread = 0;
    let notificationsUnread = 0;

    try {
      const unread = await client.getUnreadCount(); // Stream Chat method
      messagesUnread = unread.total_unread_count || 0;
      // Note: LeftMenu is the authoritative source for message counts via real-time events
      // We don't dispatch here to avoid duplicate dispatches
    } catch (err) {
      console.warn("Failed to fetch unread messages count:", err);
    }

    if (!skipNotifications) {
      try {
        const notificationsRes = await getUnreadNotificationsCount(userId);
        notificationsUnread = Number(notificationsRes.unreadCount || 0);
        // Only dispatch if count changed
        if (lastNotificationsCount.current !== notificationsUnread) {
          lastNotificationsCount.current = notificationsUnread;
          dispatch(updateUnreadNotificationsCount(notificationsUnread));
        }
      } catch (err) {
        console.warn("Failed to fetch unread notifications count:", err);
      }
    }

    const totalUnread =
      messagesUnread + (skipNotifications ? 0 : notificationsUnread);

    try {
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        if (registration.active) {
          registration.active.postMessage({
            type: "UPDATE_BADGE",
            count: totalUnread,
          });
        } else {
          await updateBadge(totalUnread);
        }
      } else {
        await updateBadge(totalUnread);
      }
    } catch (err) {
      console.warn("Failed to update badge:", err);
      await updateBadge(totalUnread);
    }
  }, [
    userId,
    client,
    badgeCleared,
    setBadgeCleared,
    skipNotifications,
    dispatch,
    updateBadge,
  ]);

  return updateAllBadges;
};
